import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { getMisVisitas, getMisQR, changePassword, getReports, createApelacion, getResidentesForTags, getApelaciones } from '../api';
import Header from '../components/Header';
import QRGenerator from '../components/QRGenerator';
import VisitItem from '../components/VisitItem';
import ReporteForm from '../components/ReporteForm';
import HashtagBadge from '../components/HashtagBadge';
import { storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { translateError } from '../utils';

export default function ResidentPanel() {
  const { user } = useAuth();
  const [view, setView] = useState('menu');
  const [visitas, setVisitas] = useState([]);
  const [reportesMap, setReportesMap] = useState({});
  const [reportes, setReportes] = useState([]);
  const [misQR, setMisQR] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Apelaciones
  const [apelacionesMap, setApelacionesMap] = useState({});

  // Cambiar contraseña
  const [showPassword, setShowPassword] = useState(false);
  const [passActual, setPassActual] = useState('');
  const [passNueva, setPassNueva] = useState('');
  const [passMsg, setPassMsg] = useState('');

  const loadVisitas = async () => {
    setLoading(true);
    setError('');
    try {
      const [data, reps] = await Promise.all([getMisVisitas(), getReports()]);
      setVisitas(data);
      const map = {};
      reps.forEach(r => {
        if (r.visitaId && r.residenteId === user.uid) {
          if (!map[r.visitaId]) map[r.visitaId] = [];
          map[r.visitaId].push(r);
        }
      });
      setReportesMap(map);
    } catch (err) {
      setError(translateError(err));
    }
    setLoading(false);
  };

  const loadMisQR = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getMisQR();
      setMisQR(data);
    } catch (err) {
      setError(translateError(err));
    }
    setLoading(false);
  };

  const loadReportes = async () => {
    setLoading(true); setError('');
    try {
      const [data, apels] = await Promise.all([getReports(), getApelaciones()]);
      // Mostrar: reportes de vigilancia sobre este residente + reportes generales de este residente
      setReportes(data.filter(r => {
        const esVigilancia = (!r.tipo || r.tipo === 'vigilancia') && r.residenteId === user.uid;
        const esGeneral = r.tipo === 'general' && r.residenteId === user.uid;
        return esVigilancia || esGeneral;
      }));
      // Mapa de apelaciones por reporteId
      const map = {};
      apels.forEach(a => {
        if (a.reporteId) {
          if (!map[a.reporteId]) map[a.reporteId] = [];
          map[a.reporteId].push(a);
        }
      });
      setApelacionesMap(map);
    } catch (err) { setError(translateError(err)); }
    setLoading(false);
  };

  const handleCambiarPassword = async (e) => {
    e.preventDefault();
    setPassMsg('');
    try {
      await changePassword(passActual, passNueva);
      setPassMsg('Contraseña actualizada exitosamente');
      setPassActual('');
      setPassNueva('');
    } catch (err) {
      setPassMsg(err.message);
    }
  };

  const renderMenu = () => (
    <div className="main-content">
      <div className="page-header">
        <h1>Mi Panel</h1>
        <p>Gestiona las visitas a tu apartamento</p>
      </div>
      <div className="profile-card">
        <div className="profile-greeting">Bienvenido(a) de vuelta</div>
        <div className="profile-name">{user.nombre_completo}</div>
        <div className="profile-detail">
          <span>🏠 Apto {user.apartamento} — Piso {user.piso}</span>
          <span style={{ marginLeft: '16px' }}>📱 {user.celular}</span>
        </div>
      </div>
      <div className="menu-grid">
        <button className="menu-item" onClick={() => setView('generarQR')}>
          <span className="menu-icon">📱</span>
          <span className="menu-label">Generar QR de Visita</span>
          <span className="menu-desc">Crea un pase digital para tu visitante</span>
        </button>
        <button className="menu-item" onClick={() => { setView('misQR'); loadMisQR(); }}>
          <span className="menu-icon">🔲</span>
          <span className="menu-label">Mis QR Generados</span>
          <span className="menu-desc">Revisa los pases que has creado</span>
        </button>
        <button className="menu-item" onClick={() => { setView('visitas'); loadVisitas(); }}>
          <span className="menu-icon">📋</span>
          <span className="menu-label">Historial de Visitas</span>
          <span className="menu-desc">Consultas quién ha entrado</span>
        </button>
        <button className="menu-item" onClick={() => { setView('reportes'); loadReportes(); }}>
          <span className="menu-icon">🚩</span>
          <span className="menu-label">Reportes</span>
          <span className="menu-desc">Anomalías e incidencias</span>
        </button>
        <button className="menu-item" onClick={() => setView('nuevoReporte')}>
          <span className="menu-icon">📝</span>
          <span className="menu-label">Nuevo Reporte</span>
          <span className="menu-desc">Averías, ruidos, estacionamiento y más</span>
        </button>
        <button className="menu-item" onClick={() => setView('password')}>
          <span className="menu-icon">🔒</span>
          <span className="menu-label">Cambiar Contraseña</span>
          <span className="menu-desc">Actualiza tu clave de acceso</span>
        </button>
      </div>
    </div>
  );

  const renderVisitas = () => (
    <div className="main-content">
      <div className="page-header flex-between">
        <div>
          <h1>Historial de Visitas</h1>
          <p>{visitas.length} visitas a tu apartamento</p>
        </div>
        <button className="btn btn-outline" onClick={() => setView('menu')}>Volver</button>
      </div>
      {loading && <div className="spinner" />}
      {error && <div className="alert alert-error">⚠️ {error}</div>}
      {!loading && visitas.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>Sin visitas aún</h3>
          <p>No tienes visitas registradas</p>
        </div>
      )}
      {visitas.map((v) => (
        <VisitItem key={v.id} visita={v} reports={reportesMap[v.id] || []} />
      ))}
    </div>
  );

  const renderMisQR = () => (
    <div className="main-content">
      <div className="page-header flex-between">
        <div>
          <h1>Mis QR Generados</h1>
          <p>{misQR.length} pases digitales creados</p>
        </div>
        <button className="btn btn-outline" onClick={() => setView('menu')}>Volver</button>
      </div>
      {loading && <div className="spinner" />}
      {!loading && misQR.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">🔲</div>
          <h3>Sin QR generados</h3>
          <p>Aún no has creado ningún pase de visita</p>
        </div>
      )}
      {misQR.map((qr) => {
        const visitantes = qr.visitantes || [{ nombre: qr.visitante_nombre, cedula: qr.visitante_cedula }];
        return (
        <div key={qr.id} className="list-item">
          <div className="info">
            <div className="name">
              {visitantes.length === 1 ? visitantes[0].nombre : `${visitantes.length} visitantes`}
            </div>
            <div className="detail">
              {visitantes.map((vis, i) => (
                <span key={i}>👤 {vis.nombre} · 🪪 {vis.cedula}{i < visitantes.length - 1 ? ' | ' : ''}</span>
              ))}
              {(() => { const p = qr.placas || (qr.placa_vehiculo ? [qr.placa_vehiculo] : []); return p.length > 0 && <span> · 🚗 {p.join(', ')}</span>; })()}
            </div>
            <div className="detail">📅 Vence: {qr.fecha_caducidad}</div>
          </div>
          <span className={`badge ${qr.usado ? 'badge-success' : 'badge-info'}`}>
            {qr.usado ? 'Usado' : 'Activo'}
          </span>
        </div>
        );
      })}
    </div>
  );

  const renderPassword = () => (
    <div className="main-content">
      <div className="page-header flex-between">
        <div>
          <h1>Cambiar Contraseña</h1>
          <p>Actualiza tu clave de acceso al sistema</p>
        </div>
        <button className="btn btn-outline" onClick={() => setView('menu')}>Volver</button>
      </div>
      <div className="card" style={{ maxWidth: '480px' }}>
        <div className="card-header">
          <h2>🔒 Nueva contraseña</h2>
        </div>
        {passMsg && (
          <div className={`alert ${passMsg.includes('exitosamente') ? 'alert-success' : 'alert-error'}`}>
            {passMsg}
          </div>
        )}
        <form onSubmit={handleCambiarPassword}>
          <div className="form-group">
            <label>Contraseña actual</label>
            <input type="password" value={passActual} onChange={(e) => setPassActual(e.target.value)} placeholder="••••••••" required />
          </div>
          <div className="form-group">
            <label>Nueva contraseña</label>
            <input type="password" value={passNueva} onChange={(e) => setPassNueva(e.target.value)} placeholder="Elige una nueva clave" required />
          </div>
          <button type="submit" className="btn btn-primary btn-full">Actualizar Contraseña</button>
        </form>
      </div>
    </div>
  );

  const CAT_COLORS = {
    averia: '#f59e0b', ruido: '#ef4444', dano: '#dc2626',
    estacionamiento: '#6366f1', mascota: '#8b5cf6', basura: '#06b6d4',
    seguridad: '#ef4444', otro: '#64748b', anomalia: '#ef4444'
  };
  const CAT_LABELS = {
    averia: '🔧 Avería', ruido: '🔊 Ruido', dano: '💥 Daño',
    estacionamiento: '🅿️ Estacionamiento', mascota: '🐕 Mascota',
    basura: '🗑️ Basura', seguridad: '🔐 Seguridad', otro: '📌 Otro',
    anomalia: '🚩 Anomalía'
  };

  const [tabReportes, setTabReportes] = useState('todos');

  // Apelación
  const [showApelacion, setShowApelacion] = useState(false);
  const [apelacionReporte, setApelacionReporte] = useState(null);
  const [apelacionTexto, setApelacionTexto] = useState('');
  const [apelacionFotos, setApelacionFotos] = useState([]);
  const [apelacionLoading, setApelacionLoading] = useState(false);
  const apelacionFileRef = useRef(null);
  const apelacionTextoRef = useRef(null);
  const [apelacionSuggs, setApelacionSuggs] = useState([]);
  const [showApelacionSuggs, setShowApelacionSuggs] = useState(false);
  const [apelacionHashtags, setApelacionHashtags] = useState([]);
  const [residentesTags, setResidentesTags] = useState([]);

  // Modal de detalle de residente desde hashtag
  const [showResidenteModal, setShowResidenteModal] = useState(false);
  const [residenteDetail, setResidenteDetail] = useState(null);

  const openResidenteDetail = (hashtag) => {
    const apto = hashtag.replace(/^#/, '');
    const found = residentesTags.find(r =>
      r.apartamento?.toLowerCase() === apto.toLowerCase()
    );
    if (found) {
      setResidenteDetail(found);
      setShowResidenteModal(true);
    }
  };

  // Conteo de reportes por hashtag
  const getReportCount = (tag) => {
    const apto = tag.replace(/^#/, '').toLowerCase();
    return reportes.filter(r =>
      r.hashtags?.some(h => h.toLowerCase() === apto)
    ).length;
  };

  const getResidenteByTag = (tag) => {
    const apto = tag.replace(/^#/, '');
    return residentesTags.find(r => r.apartamento?.toLowerCase() === apto.toLowerCase()) || null;
  };

  useEffect(() => {
    getResidentesForTags().then(setResidentesTags).catch(() => {});
  }, []);

  const openApelacion = (reporte) => {
    setApelacionReporte(reporte);
    setApelacionTexto('');
    setApelacionFotos([]);
    setError('');
    setShowApelacion(true);
  };

  const handleApelacionFoto = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setApelacionFotos([...apelacionFotos, reader.result]);
      reader.readAsDataURL(file);
    }
  };

  const handleApelacionTextChange = (e) => {
    const val = e.target.value;
    setApelacionTexto(val);

    // Extraer hashtags
    const tags = [];
    const regex = /#(\w+)/g;
    let match;
    while ((match = regex.exec(val)) !== null) {
      tags.push(match[1]);
    }
    setApelacionHashtags([...new Set(tags)]);

    // Autocompletado
    const pos = e.target.selectionStart;
    const beforeCursor = val.substring(0, pos);
    const hashIndex = beforeCursor.lastIndexOf('#');
    if (hashIndex !== -1) {
      const afterHash = beforeCursor.substring(hashIndex + 1);
      if (!afterHash.includes(' ') && !afterHash.includes('\n')) {
        const search = afterHash.toLowerCase();
        const filtered = residentesTags.filter(r =>
          r.apartamento?.toLowerCase().startsWith(search) ||
          r.nombre_completo?.toLowerCase().includes(search)
        ).slice(0, 6);
        setApelacionSuggs(filtered);
        setShowApelacionSuggs(filtered.length > 0);
        return;
      }
    }
    setShowApelacionSuggs(false);
  };

  const selectApelacionSug = (residente) => {
    const val = apelacionTexto;
    const textarea = apelacionTextoRef.current;
    if (!textarea) return;
    const pos = textarea.selectionStart;
    const beforeCursor = val.substring(0, pos);
    const hashIndex = beforeCursor.lastIndexOf('#');
    const before = val.substring(0, hashIndex);
    const after = val.substring(pos);
    const tag = residente.apartamento?.replace(/\s+/g, '') || 'apto';
    const newVal = `${before}#${tag} ${after}`;
    setApelacionTexto(newVal);
    setShowApelacionSuggs(false);
    
    const tags = [];
    const regex = /#(\w+)/g;
    let match;
    while ((match = regex.exec(newVal)) !== null) {
      tags.push(match[1]);
    }
    setApelacionHashtags([...new Set(tags)]);
    
    setTimeout(() => {
      textarea.focus();
      const newPos = before.length + tag.length + 2;
      textarea.setSelectionRange(newPos, newPos);
    }, 0);
  };

  const submitApelacion = async () => {
    if (!apelacionTexto.trim() && apelacionFotos.length === 0) {
      setError('Escribe un texto o adjunta al menos una foto');
      return;
    }
    setApelacionLoading(true); setError('');
    try {
      const fotoUrls = [];
      for (const foto of apelacionFotos) {
        const ts = Date.now();
        const r = ref(storage, `apelaciones/${apelacionReporte.id}_${ts}.jpg`);
        const blob = await fetch(foto).then(r => r.blob());
        const snap = await uploadBytes(r, blob);
        fotoUrls.push(await getDownloadURL(snap.ref));
      }
      await createApelacion({
        reporteId: apelacionReporte.id,
        residente_nombre: user.nombre_completo,
        texto: apelacionTexto,
        fotos: fotoUrls,
        hashtags: apelacionHashtags
      });
      setShowApelacion(false);
      // Actualizar mapa local inmediatamente
      setApelacionesMap(prev => ({
        ...prev,
        [apelacionReporte.id]: [...(prev[apelacionReporte.id] || []), { texto: apelacionTexto }]
      }));
      loadReportes();
    } catch (err) { setError(translateError(err)); }
    setApelacionLoading(false);
  };

  const renderReportes = () => {
    const enviados = reportes.filter(r => r.tipo === 'general' && r.residenteId === user.uid);
    const recibidos = reportes.filter(r => (!r.tipo || r.tipo === 'vigilancia') && r.residenteId === user.uid);
    const mostrar = tabReportes === 'enviados' ? enviados : tabReportes === 'recibidos' ? recibidos : reportes;

    return (
    <div className="main-content">
      <div className="page-header flex-between">
        <div>
          <h1>🚩 Reportes</h1>
          <p>{reportes.length} reportes</p>
        </div>
        <button className="btn btn-outline" onClick={() => setView('menu')}>Volver</button>
      </div>
      {loading && <div className="spinner" />}

      {/* Tabs de filtro */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[
          { key: 'todos', label: '📋 Todos', count: reportes.length, color: 'var(--primary)' },
          { key: 'enviados', label: '📤 Enviados por ti', count: enviados.length, color: 'var(--accent)' },
          { key: 'recibidos', label: '📥 Recibidos', count: recibidos.length, color: 'var(--danger)' },
        ].map((tab) => (
          <button key={tab.key} onClick={() => setTabReportes(tab.key)}
            style={{
              padding: '10px 20px', borderRadius: 24, border: tabReportes === tab.key
                ? `2px solid ${tab.color}` : '1px solid var(--border)',
              background: tabReportes === tab.key ? `${tab.color}12` : 'rgba(255,255,255,0.4)',
              color: tabReportes === tab.key ? tab.color : 'var(--text-secondary)',
              fontWeight: tabReportes === tab.key ? 700 : 500,
              fontSize: '0.82rem', cursor: 'pointer', transition: 'all 0.2s',
              display: 'flex', alignItems: 'center', gap: 6
            }}>
            {tab.label}
            <span style={{
              background: tabReportes === tab.key ? tab.color : 'var(--border)',
              color: tabReportes === tab.key ? '#fff' : 'var(--text-secondary)',
              borderRadius: 12, padding: '1px 8px', fontSize: '0.7rem', fontWeight: 700
            }}>{tab.count}</span>
          </button>
        ))}
      </div>

      {!loading && mostrar.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">
            {tabReportes === 'enviados' ? '📤' : tabReportes === 'recibidos' ? '📥' : '✅'}
          </div>
          <h3>Sin reportes</h3>
          <p>
            {tabReportes === 'enviados' ? 'No has enviado reportes aún. Usa "Nuevo Reporte" para reportar averías, ruidos y más.' :
             tabReportes === 'recibidos' ? 'No has recibido reportes de vigilancia.' :
             'No hay reportes relacionados contigo'}
          </p>
        </div>
      )}
      {mostrar.map((r) => {
        const catColor = CAT_COLORS[r.categoria] || '#ef4444';
        const esMio = r.tipo === 'general';
        return (
        <div key={r.id} className="history-item" style={{
          borderLeftColor: esMio ? 'var(--accent)' : catColor,
          background: esMio ? 'linear-gradient(135deg, rgba(245,158,11,0.03), rgba(255,255,255,0.7))' : undefined
        }}>
          <div className="visit-header">
            <div className="visit-name">
              {r.tipo === 'vigilancia' || !r.tipo ? '🚩 Reporte de anomalía (vigilancia)' : CAT_LABELS[r.categoria] || '📌 Reporte'}
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span className="badge" style={{
                background: esMio ? 'var(--accent)' : 'var(--danger)',
                color: '#fff', fontSize: '0.7rem'
              }}>
                {esMio ? '📤 Enviado' : '📥 Recibido'}
              </span>
              {!esMio && (
                apelacionesMap[r.id]?.length > 0 ? (
                  <span style={{
                    color: 'var(--success)', fontSize: '0.75rem', fontWeight: 700,
                    display: 'flex', alignItems: 'center', gap: 4
                  }}>
                    ✅ Apelación enviada
                  </span>
                ) : (
                <button className="btn btn-ghost btn-sm"
                  onClick={(e) => { e.stopPropagation(); openApelacion(r); }}
                  style={{ color: 'var(--info)', fontSize: '0.75rem', fontWeight: 700 }}>
                  💬 Apelar
                </button>
                )
              )}
            </div>
          </div>
          {(r.vigilante_nombre || r.autor_nombre) && (
            <div className="visit-detail">
              <span>{r.tipo === 'vigilancia' || !r.tipo ? '🛡️' : '👤'} {r.vigilante_nombre || r.autor_nombre}</span>
            </div>
          )}
          {r.texto && (
            <div className="visit-detail">
              <span>📝 {r.texto}</span>
            </div>
          )}
          {r.hashtags && r.hashtags.length > 0 && (
            <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
              {r.hashtags.map((tag) => (
                <HashtagBadge key={tag} tag={tag}
                  residente={getResidenteByTag(tag)}
                  reportCount={getReportCount(tag)}
                  onOpenDetail={openResidenteDetail} />
              ))}
            </div>
          )}
          {r.fotos && r.fotos.length > 0 && (
            <div style={{display:'flex',gap:8,marginTop:8,flexWrap:'wrap'}}>
              {r.fotos.map((f, i) => (
                <a key={i} href={f} target="_blank" rel="noopener noreferrer">
                  <img src={f} alt={`Evidencia ${i+1}`} style={{width:64,height:64,borderRadius:8,objectFit:'cover',border:'2px solid var(--border)',cursor:'pointer'}} />
                </a>
              ))}
            </div>
          )}
          <div className="visit-date">📅 {r.createdAt?.toDate ? new Date(r.createdAt.toDate()).toLocaleString('es-CO') : ''}</div>
        </div>
        );
      })}
    </div>
    );
  };

  return (
    <>
      <Header />
      {view === 'menu' && renderMenu()}
      {view === 'generarQR' && <QRGenerator onBack={() => setView('menu')} />}
      {view === 'nuevoReporte' && <ReporteForm user={user} onBack={() => setView('menu')} onSuccess={() => setView('menu')} />}
      {view === 'visitas' && renderVisitas()}
      {view === 'misQR' && renderMisQR()}
      {view === 'password' && renderPassword()}
      {view === 'reportes' && renderReportes()}

      {/* Modal de Apelación */}
      {showApelacion && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          background: 'rgba(0,0,0,0.7)', zIndex: 10000, display: 'flex',
          alignItems: 'center', justifyContent: 'center', padding: 20
        }} onClick={() => setShowApelacion(false)}>
          <div className="card" style={{ maxWidth: 520, width: '100%', maxHeight: '90vh', overflow: 'auto' }}
            onClick={(e) => e.stopPropagation()}>
            <div className="card-header">
              <h2>💬 Apelar Reporte</h2>
            </div>
            {apelacionReporte && (
              <div className="mb-16" style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                <p style={{ fontWeight: 600, color: 'var(--text)' }}>
                  {(!apelacionReporte.tipo || apelacionReporte.tipo === 'vigilancia')
                    ? '🚩 Reporte de anomalía' : CAT_LABELS[apelacionReporte.categoria] || 'Reporte'}
                </p>
                {apelacionReporte.texto && <p>📝 {apelacionReporte.texto}</p>}
              </div>
            )}
            <div className="form-group">
              <label>Tu respuesta</label>
              <div style={{ position: 'relative' }}>
                <textarea ref={apelacionTextoRef}
                  value={apelacionTexto} onChange={handleApelacionTextChange}
                  placeholder="Explica tu versión, ofrece una disculpa, propón una solución o aclara que no eres responsable... Usa # para etiquetar un apartamento"
                  rows={4} style={{ width: '100%', borderRadius: 'var(--radius-sm)', padding: 12, border: '1px solid var(--border)', fontFamily: 'inherit' }} />
                {showApelacionSuggs && apelacionSuggs.length > 0 && (
                  <div style={{
                    position: 'absolute', bottom: '100%', left: 0, right: 0,
                    background: '#fff', borderRadius: 12, boxShadow: '0 10px 40px rgba(0,0,0,0.18)',
                    border: '1px solid var(--border)', zIndex: 50, maxHeight: 180,
                    overflow: 'auto', marginBottom: 4
                  }}>
                    {apelacionSuggs.map((r) => (
                      <div key={r.id}
                        onMouseDown={(e) => { e.preventDefault(); selectApelacionSug(r); }}
                        style={{
                          padding: '10px 14px', cursor: 'pointer', display: 'flex',
                          alignItems: 'center', gap: 8, borderBottom: '1px solid #f8fafc',
                          transition: 'background 0.1s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(99,102,241,0.06)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                        <span style={{ fontWeight: 700, color: 'var(--primary)', fontSize: '0.85rem' }}>#{r.apartamento}</span>
                        <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{r.nombre_completo?.split(' ')[0]} · P{r.piso}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <p style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                💡 Escribe <strong>#</strong> para etiquetar un apartamento
              </p>
              {apelacionHashtags.length > 0 && (
                <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                  {apelacionHashtags.map((tag) => (
                    <span key={tag} style={{
                      background: 'linear-gradient(135deg, rgba(99,102,241,0.1), rgba(139,92,246,0.06))',
                      color: 'var(--primary)', borderRadius: 20, fontWeight: 600,
                      fontSize: '0.75rem', padding: '4px 14px',
                      border: '1px solid rgba(99,102,241,0.2)'
                    }}>
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="mb-16">
              <label style={{ fontWeight: 700, fontSize: '0.78rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 8, display: 'block' }}>
                📸 Fotos (opcional)
              </label>
              {apelacionFotos.length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                  {apelacionFotos.map((f, i) => (
                    <div key={i} style={{ position: 'relative' }}>
                      <img src={f} alt={`Foto ${i + 1}`} style={{ width: 72, height: 72, borderRadius: 8, objectFit: 'cover', border: '2px solid var(--border)' }} />
                      <button type="button" className="btn btn-ghost btn-sm"
                        onClick={() => setApelacionFotos(apelacionFotos.filter((_, j) => j !== i))}
                        style={{ position: 'absolute', top: -6, right: -6, background: 'var(--danger)', color: '#fff', borderRadius: '50%', width: 22, height: 22, fontSize: '0.6rem', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <input ref={apelacionFileRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handleApelacionFoto} />
              <button type="button" className="btn btn-outline btn-full" onClick={() => apelacionFileRef.current?.click()}>
                📎 Adjuntar foto
              </button>
            </div>
            {error && <div className="alert alert-error">⚠️ {error}</div>}
            <div className="btn-row mt-16">
              <button className="btn btn-outline" onClick={() => setShowApelacion(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={submitApelacion} disabled={apelacionLoading}>
                {apelacionLoading ? 'Enviando...' : '💬 Enviar Apelación'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de detalle de residente desde hashtag */}
      {showResidenteModal && residenteDetail && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          background: 'rgba(0,0,0,0.6)', zIndex: 10000, display: 'flex',
          alignItems: 'center', justifyContent: 'center', padding: 20
        }} onClick={() => setShowResidenteModal(false)}>
          <div className="card" style={{ maxWidth: 420, width: '100%' }}
            onClick={(e) => e.stopPropagation()}>
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2>🏠 #{residenteDetail.apartamento}</h2>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowResidenteModal(false)}>✕</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 56, height: 56, borderRadius: 16,
                  background: 'linear-gradient(135deg, var(--primary), var(--primary-2))',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontSize: '1.5rem', fontWeight: 700
                }}>
                  {(residenteDetail.nombre_completo || '')[0]?.toUpperCase() || '🏠'}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>{residenteDetail.nombre_completo}</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>Residente</div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div style={{ padding: 12, background: 'rgba(99,102,241,0.04)', borderRadius: 10 }}>
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: 600 }}>Apartamento</div>
                  <div style={{ fontWeight: 700, fontSize: '1rem' }}>{residenteDetail.apartamento}</div>
                </div>
                <div style={{ padding: 12, background: 'rgba(99,102,241,0.04)', borderRadius: 10 }}>
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: 600 }}>Piso</div>
                  <div style={{ fontWeight: 700, fontSize: '1rem' }}>P{residenteDetail.piso}</div>
                </div>
                <div style={{ padding: 12, background: 'rgba(99,102,241,0.04)', borderRadius: 10 }}>
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: 600 }}>Cédula</div>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>🪪 {residenteDetail.cedula}</div>
                </div>
                <div style={{ padding: 12, background: 'rgba(99,102,241,0.04)', borderRadius: 10 }}>
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: 600 }}>Celular</div>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>📱 {residenteDetail.celular}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}